/**
 * Hook để load lịch sử chat của tất cả 5 agents khi lần đầu vào app
 */
import { useRef, useCallback } from 'react';
import { ChatAgentType } from '@constants/chat-agents';
import { chatService } from '@services/chatService';
import type { ChatMessage } from '../types';
import { isAbortError, useAbortableRequest } from './useAbortableRequest';

const ALL_AGENTS = Object.values(ChatAgentType);

interface UseInitialHistoryLoaderProps {
  onHistoriesLoaded: (
    histories: Record<ChatAgentType, ChatMessage[]>,
    cursors: Record<ChatAgentType, string | null>,
    hasChatHistories: Record<ChatAgentType, boolean>
  ) => void;
}

export type LoadAllHistoriesOptions = {
  /** Abort previous load and always fetch again for this token */
  force?: boolean;
};

export const useInitialHistoryLoader = ({ onHistoriesLoaded }: UseInitialHistoryLoaderProps) => {
  const isLoadingRef = useRef(false);
  const hasLoadedRef = useRef(false);
  const lastLoadedTokenRef = useRef<string | null>(null);
  const { abort, next, isCurrent } = useAbortableRequest();

  const abortInFlight = useCallback(() => {
    abort();
    isLoadingRef.current = false;
  }, [abort]);

  const resetForNewToken = useCallback(() => {
    abortInFlight();
    hasLoadedRef.current = false;
    lastLoadedTokenRef.current = null;
  }, [abortInFlight]);

  const loadAllHistories = useCallback(async (
    token: string,
    options?: LoadAllHistoriesOptions,
  ) => {
    const force = Boolean(options?.force);

    if (force || lastLoadedTokenRef.current !== token) {
      abortInFlight();
      hasLoadedRef.current = false;
      lastLoadedTokenRef.current = token;
    }

    // Same token already loaded / loading — skip unless force
    if (!force && (isLoadingRef.current || hasLoadedRef.current)) {
      return;
    }

    // Force while same controller would still be loading: start fresh
    if (force && isLoadingRef.current) {
      abortInFlight();
    }

    const controller = next();
    isLoadingRef.current = true;
    lastLoadedTokenRef.current = token;

    try {
      const results = await Promise.allSettled(
        ALL_AGENTS.map(async (agentType) => {
          const response = await chatService.getChatHistory(
            token,
            '',
            agentType,
            null,
            { signal: controller.signal },
          );
          return {
            agentType,
            data: response?.data?.data || [],
            nextCursor: response?.data?.nextCursor || null,
          };
        })
      );

      if (controller.signal.aborted) {
        return;
      }

      const histories: Record<ChatAgentType, ChatMessage[]> = {
        [ChatAgentType.AI]: [],
        [ChatAgentType.CSKH]: [],
        [ChatAgentType.TAX_QUESTION]: [],
        [ChatAgentType.INSTRUCTION_MANUAL]: [],
        [ChatAgentType.ANALYTICS_QUESTION]: [],
      };

      const cursors: Record<ChatAgentType, string | null> = {
        [ChatAgentType.AI]: null,
        [ChatAgentType.CSKH]: null,
        [ChatAgentType.TAX_QUESTION]: null,
        [ChatAgentType.INSTRUCTION_MANUAL]: null,
        [ChatAgentType.ANALYTICS_QUESTION]: null,
      };

      const hasChatHistories: Record<ChatAgentType, boolean> = {
        [ChatAgentType.AI]: false,
        [ChatAgentType.CSKH]: false,
        [ChatAgentType.TAX_QUESTION]: false,
        [ChatAgentType.INSTRUCTION_MANUAL]: false,
        [ChatAgentType.ANALYTICS_QUESTION]: false,
      };

      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          const { agentType, data, nextCursor } = result.value;
          const messages = chatService.parseHistoryMessages(data, agentType);
          histories[agentType] = messages;
          cursors[agentType] = nextCursor;
          hasChatHistories[agentType] = messages.length > 0;
        } else if (!isAbortError(result.reason)) {
          console.error(`[InitialLoader] Failed to load history:`, result.reason);
        }
      });

      if (controller.signal.aborted) {
        return;
      }

      onHistoriesLoaded(histories, cursors, hasChatHistories);
      hasLoadedRef.current = true;
    } catch (error) {
      if (!isAbortError(error)) {
        console.error('[InitialLoader] Error loading histories:', error);
      }
    } finally {
      if (isCurrent(controller)) {
        isLoadingRef.current = false;
      }
    }
  }, [onHistoriesLoaded, abortInFlight, next, isCurrent]);

  return { loadAllHistories, resetForNewToken, abortInFlight };
};
